import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Shield, Lock, Mail, Loader2, KeyRound } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from '../../hooks/use-toast';
import { portalPath } from '../../lib/portal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const GovLogin = () => {
  const [form, setForm] = useState({ email: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [mfaStep, setMfaStep] = useState(null); // { mfa_token, email }
  const [mfaCode, setMfaCode] = useState('');
  const { login } = useAuth();
  const navigate = useNavigate();

  const completeSignIn = async (loginRes) => {
    const access_token = loginRes.access_token;
    const me = await axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${access_token}` } });
    // Merge the must_change_password flag from the login response into the user object
    const userData = { ...me.data, must_change_password: !!loginRes.must_change_password };
    login(userData, access_token);
    toast({ title: 'Welcome', description: `Signed in as ${me.data.name}` });
    if (loginRes.must_change_password) {
      navigate(portalPath('/change-password?forced=1'), { replace: true });
    } else if (userData.role === 'uske_delivery') {
      navigate('/delivery');
    } else if (userData.role === 'uske_front_desk' || userData.role === 'uske_finance_officer') {
      navigate(portalPath('/fulfilment'));
    } else {
      navigate(portalPath('/dashboard'));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/login`, form);
      if (res.data.mfa_required) {
        setMfaStep({ mfa_token: res.data.mfa_token, email: res.data.email });
        toast({ title: 'Two-factor required', description: 'Enter your authenticator code.' });
      } else {
        await completeSignIn(res.data);
      }
    } catch (err) {
      toast({ title: 'Sign in failed', description: err.response?.data?.detail || 'Invalid email or password', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const submitMfa = async (e) => {
    e.preventDefault();
    if (mfaCode.length < 6) return toast({ title: 'Enter the 6-digit code', variant: 'destructive' });
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/login/mfa`, { mfa_token: mfaStep.mfa_token, code: mfaCode });
      await completeSignIn(res.data);
    } catch (err) {
      toast({ title: 'Invalid code', description: err.response?.data?.detail || 'Please try again', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-950 via-blue-900 to-slate-900 flex items-center justify-center px-4 py-10" data-testid="gov-login-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-6 text-white">
          <div className="inline-flex items-center justify-center h-14 w-14 rounded-lg bg-white text-blue-900 mb-3"><Shield className="h-7 w-7" /></div>
          <h1 className="text-2xl font-bold">USKE Procurement Portal</h1>
          <p className="text-blue-200 text-sm mt-1">Washington State Contract 09925 · USKE Supply LLC</p>
        </div>

        <div className="bg-white rounded-xl shadow-xl p-6 sm:p-8 border border-blue-100">
          {!mfaStep ? (
            <>
              <h2 className="text-lg font-semibold text-slate-800 mb-4">Authorized User Sign In</h2>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="email">Government Email</Label>
                  <div className="relative mt-1">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input id="email" type="email" className="pl-10" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required data-testid="gov-login-email" />
                  </div>
                </div>
                <div>
                  <Label htmlFor="password">Password</Label>
                  <div className="relative mt-1">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input id="password" type="password" className="pl-10" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required data-testid="gov-login-password" />
                  </div>
                </div>
                <Button type="submit" disabled={loading} className="w-full bg-blue-700 hover:bg-blue-800 text-white" data-testid="gov-login-submit">
                  {loading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Signing in...</> : 'Sign In'}
                </Button>
              </form>

              <div className="mt-5 pt-5 border-t text-center text-xs text-slate-500 space-y-1">
                <div><Link to={portalPath('/forgot-password')} className="text-blue-700 underline" data-testid="gov-forgot-password-link">Forgot your password?</Link></div>
                <div>New agency? <Link to={`${portalPath('/')}#enroll`} className="text-blue-700 underline">Request access</Link>.</div>
              </div>
            </>
          ) : (
            <>
              <div className="flex items-center gap-2 mb-3 text-blue-700"><KeyRound className="h-5 w-5" /><h2 className="text-lg font-semibold text-slate-800">Two-Factor Code</h2></div>
              <p className="text-sm text-slate-500 mb-4">Open your authenticator app and enter the 6-digit code for <b>{mfaStep.email}</b>.</p>
              <form onSubmit={submitMfa} className="space-y-4">
                <Input inputMode="numeric" maxLength={6} placeholder="123456" value={mfaCode} onChange={(e) => setMfaCode(e.target.value)} required data-testid="gov-login-mfa-code" autoFocus />
                <Button type="submit" disabled={loading} className="w-full bg-blue-700 hover:bg-blue-800 text-white" data-testid="gov-login-mfa-submit">
                  {loading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Verifying...</> : 'Verify and continue'}
                </Button>
                <Button type="button" variant="ghost" className="w-full" onClick={() => { setMfaStep(null); setMfaCode(''); }}>Back</Button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default GovLogin;
